import { Component } from '@theme/component';

const NUMBER_PATTERN = /[\d.,]+/;

/**
 * Add-on picker of the variant-e / pdp-v2 buy box (blocks/product-configurator-e.liquid).
 *
 * Adds the ticked add-ons to the header and sticky-pill prices and hands the unit prices to
 * the optional summary. The cart needs nothing from here: product-form.js adds every ticked
 * `.additional-test__checkbox` as its own line item.
 *
 * Summary only exists with show_summary, so every lookup is optional.
 * product-configurator.js isn't loaded: it writes the same header/sticky nodes.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement[]} [addonCheckboxes]
 *
 * @extends {Component<Refs>}
 */
class ProductConfiguratorEComponent extends Component {
  /**
   * @type {{
   *   price: HTMLElement, priceText: string, basePrice: number,
   *   compare: HTMLElement | null, compareText: string, baseCompare: number,
   *   tag: HTMLElement | null, decimalSeparator: string, isStatic: boolean
   * } | null}
   */
  #header = null;

  /** @type {{ element: HTMLElement, text: string } | null} */
  #sticky = null;

  connectedCallback() {
    super.connectedCallback();

    const price = /** @type {HTMLElement | null} */ (document.querySelector('.product-details-header__price'));
    if (price) {
      const compare = /** @type {HTMLElement | null} */ (
        document.querySelector('.product-details-header__discount-price')
      );
      const priceText = price.textContent ?? '';
      const basePrice = Number(price.dataset.productprice);

      // The compare-at line only renders when compare_at > price. Without it the
      // compare-at falls back to the base price, so the summary still fills and
      // its discount/savings rows stay hidden.
      this.#header = {
        price,
        priceText,
        basePrice,
        compare,
        compareText: compare?.textContent ?? '',
        baseCompare: compare ? Number(compare.dataset.productcompareatprice) : basePrice,
        tag: document.querySelector('.product-details-header__tag-text'),
        decimalSeparator: priceText.includes(',') ? ',' : '.',
        // pdp-v2 shows a fixed "from" price; only the summary and sticky follow the add-ons.
        isStatic: price.hasAttribute('data-price-static'),
      };
    }

    // Reconciled again on pageshow: Chrome restores checkbox and quantity state after
    // this runs (still before `load`) and fires no change event.
    this.updatePrices();
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  #handlePageShow = () => this.updatePrices();

  updatePrices() {
    const header = this.#header;
    if (!header) return;

    const checked = (this.refs.addonCheckboxes ?? []).filter((checkbox) => checkbox.checked);
    const extra = checked.reduce((sum, checkbox) => sum + Number(checkbox.dataset.productprice), 0);
    // Add-ons carry their own compare-at (falls back to the price in Liquid);
    // adding the sale price here understated the savings of discounted add-ons.
    const extraCompare = checked.reduce(
      (sum, checkbox) => sum + Number(checkbox.dataset.productcompareat || checkbox.dataset.productprice),
      0
    );
    const price = header.basePrice + extra;
    const compare = header.baseCompare + extraCompare;

    if (!header.isStatic) {
      header.price.textContent = header.priceText.replace(NUMBER_PATTERN, this.#formatPrice(price));
      if (header.compare) {
        header.compare.textContent = header.compareText.replace(NUMBER_PATTERN, this.#formatPrice(compare));
      }
    }

    const summary = this.querySelector('configurator-summary-component');
    if (summary instanceof HTMLElement) {
      summary.dataset.unitPrice = String(price);
      summary.dataset.unitCompare = String(compare);
    }

    if (header.tag && !header.isStatic && compare > price) {
      const percent = Math.round(((compare - price) / compare) * 100);
      header.tag.textContent = (header.tag.textContent ?? '').replace(/\d+/, String(percent));
    }

    const sticky = this.#resolveSticky();
    if (sticky) sticky.element.textContent = sticky.text.replace(NUMBER_PATTERN, this.#formatPrice(price));
  }

  /**
   * @param {MouseEvent} event - Click on an add-on's info button.
   */
  openAddonInfo(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const dialog = document.getElementById(event.target.dataset.modalTarget ?? '');
    if (!(dialog instanceof HTMLDialogElement)) return;

    event.preventDefault();
    event.stopPropagation();

    if (typeof window.phCapture === 'function') {
      window.phCapture('pdp_addon_info_opened', {
        addon_handle: event.target.closest('.product-configurator-e__addon')?.getAttribute('data-handle') || null,
      });
    }

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  /**
   * @param {MouseEvent} event - Click on a dialog's close button.
   */
  closeAddonInfo(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    event.target.closest('dialog')?.close();
  }

  /**
   * @param {MouseEvent} event - Any click inside an add-on dialog.
   */
  closeAddonInfoOnBackdrop(event) {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;

    // `target` is proxied to the dialog for every click inside it; the real target is
    // the dialog itself only for a backdrop click.
    if (event.composedPath()[0] === dialog) dialog.close();
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #formatPrice(cents) {
    return (cents / 100).toFixed(2).replace('.', this.#header?.decimalSeparator ?? '.');
  }

  /**
   * Looked up until found and its original text cached on first sighting: the pill
   * renders after this block.
   */
  #resolveSticky() {
    if (!this.#sticky) {
      const element = /** @type {HTMLElement | null} */ (document.querySelector('.sticky-atc-d__price'));
      if (element) this.#sticky = { element, text: element.textContent ?? '' };
    }

    return this.#sticky;
  }
}

if (!customElements.get('product-configurator-e-component')) {
  customElements.define('product-configurator-e-component', ProductConfiguratorEComponent);
}
